import axios from 'axios';
import type { Product } from '@/types';
import { storage, STORAGE_KEYS } from '@/utils/storage';
import { mapWebsiteProduct, type ProductApiItem } from './website-product.service';

const websiteApiBaseUrl = (import.meta.env.VITE_WEBSITE_API_BASE_URL || 'https://api.beautysports.in/website-api').replace(/\/$/, '');

function authHeaders() {
  const token = storage.get<string | null>(STORAGE_KEYS.AUTH_TOKEN, null);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function apiError(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as { error?: { message?: string; details?: { fields?: Record<string, string> } } } | undefined;
    const rawMessage = payload?.error?.message || fallback;
    const message = error.response?.status === 401 || /authentication token/i.test(rawMessage) ? 'Login Required' : rawMessage;
    const fields = payload?.error?.details?.fields || {};
    return Object.assign(new Error(message), { fields });
  }
  return error instanceof Error ? error : new Error(fallback);
}

export const customerWishlistService = {
  async list(): Promise<Product[]> {
    try {
      const response = await axios.get<{ data: ProductApiItem[] }>(`${websiteApiBaseUrl}/customer/wishlist`, { headers: authHeaders() });
      return (response.data.data || []).map(mapWebsiteProduct);
    } catch (error) {
      throw apiError(error, 'Unable to load wishlist.');
    }
  },

  async add(productUuid: string): Promise<Product> {
    try {
      const response = await axios.post<{ data: ProductApiItem }>(`${websiteApiBaseUrl}/customer/wishlist/items`, { productUuid }, { headers: authHeaders() });
      return mapWebsiteProduct(response.data.data);
    } catch (error) {
      throw apiError(error, 'Unable to add item to wishlist.');
    }
  },

  async remove(productUuid: string): Promise<void> {
    try {
      await axios.delete(`${websiteApiBaseUrl}/customer/wishlist/items/${encodeURIComponent(productUuid)}`, { headers: authHeaders() });
    } catch (error) {
      throw apiError(error, 'Unable to remove item from wishlist.');
    }
  },
};
